/**
 * Helpers for the "Add Custom Item" dialog.
 *
 * Server / wire model:
 *   - priceType:    'fixed' | 'byWeight'
 *   - weightUnit:   'carat' | 'gram'  (only when priceType='byWeight')
 *   - weight:       Number             (only when priceType='byWeight')
 *   - pricePerUnit: Number             (only when priceType='byWeight')
 *   - finalPrice:   Number             (always; for byWeight the server computes
 *                   it from weight × pricePerUnit, so the client may omit it)
 */

import { CUSTOM_PRICE_TYPES, CUSTOM_WEIGHT_UNITS, DIAMOND_TYPES } from '../constants/appConstants';

function isPositive(n) {
  if (n === '' || n == null) return false;
  const x = Number(n);
  return Number.isFinite(x) && x > 0;
}

/**
 * @returns {boolean} true if the price/weight inputs for the given priceType
 *   are valid enough to enable the submit button.
 */
export function isPricingFormValid(state) {
  const s = state || {};
  if (s.priceType === CUSTOM_PRICE_TYPES.BY_WEIGHT) {
    const unitOk = s.weightUnit === CUSTOM_WEIGHT_UNITS.CARAT || s.weightUnit === CUSTOM_WEIGHT_UNITS.GRAM;
    return unitOk && isPositive(s.weight) && isPositive(s.pricePerUnit);
  }
  return isPositive(s.totalPrice);
}

/**
 * Build the request body for POST /api/order-items/manual-custom from dialog state.
 *
 * For byWeight, server is the source of truth for `finalPrice`, so we only send
 * the breakdown fields.
 *
 * @returns {object}
 */
export function buildManualCustomPayload(state) {
  const s = state || {};
  const base = {
    clientId: s.clientId,
    description: (s.description || '').trim(),
    priceType: s.priceType,
  };
  if (s.currency) {
    base.currency = s.currency;
  }
  if (s.priceType === CUSTOM_PRICE_TYPES.BY_WEIGHT) {
    return {
      ...base,
      weightUnit: s.weightUnit,
      weight: parseFloat(s.weight),
      pricePerUnit: parseFloat(s.pricePerUnit),
    };
  }
  return { ...base, finalPrice: parseFloat(s.totalPrice) };
}

/**
 * Build a cart line item for a custom product/service (stored locally until checkout).
 *
 * @returns {object}
 */
export function buildCartCustomItem(state) {
  const description = (state.description || '').trim();
  const priceType = state.priceType || CUSTOM_PRICE_TYPES.FIXED;
  const item = {
    type: DIAMOND_TYPES.CUSTOM,
    description,
    currency: state.currency,
    priceType,
  };

  if (priceType === CUSTOM_PRICE_TYPES.BY_WEIGHT) {
    const weight = parseFloat(state.weight);
    const pricePerUnit = parseFloat(state.pricePerUnit);
    item.weightUnit = state.weightUnit;
    item.weight = weight;
    item.pricePerUnit = pricePerUnit;
    item.finalPrice = previewWeightedTotal(state.weight, state.pricePerUnit);
  } else {
    item.finalPrice = parseFloat(state.totalPrice);
    item.pricePerUnit = item.finalPrice;
  }

  return item;
}

/** Label for price fields, e.g. "Total Price (₪)" */
export function customItemPriceLabel(kind, currencyCode, symbol) {
  const sym = symbol || currencyCode || '';
  if (kind === 'total') return `Total Price (${sym})`;
  if (kind === 'perUnit') return `Price (${sym}`;
  return `Price (${sym})`;
}

/**
 * Round to 2 decimal places. Mirrors the server-side helper so the live total
 * preview matches what the server ultimately stores.
 *
 * @returns {number|null} weight × pricePerUnit (cents-rounded), or null if the
 *   inputs aren't both positive numbers.
 */
export function previewWeightedTotal(weight, pricePerUnit) {
  const w = Number(weight);
  const p = Number(pricePerUnit);
  if (!Number.isFinite(w) || !Number.isFinite(p) || w <= 0 || p <= 0) return null;
  return Math.round(w * p * 100) / 100;
}

/** Short label for a weight unit ("ct" or "g"). */
export function weightUnitShort(unit) {
  if (unit === CUSTOM_WEIGHT_UNITS.CARAT) return 'ct';
  if (unit === CUSTOM_WEIGHT_UNITS.GRAM) return 'g';
  return '';
}
